// The rules a `gift.types` list has to obey. They live beside the editor rather
// than inside it, for the reason `helpTypes.ts` gives: ProjectConfigPage lifts
// the same rules to the save bar, and a module that exports both a component
// and a validator cannot be hot-replaced.

import {
  AMOUNT_MAX,
  AMOUNT_MIN,
  MAX_MAX,
  MAX_MIN,
  TYPE_REGEX,
  WINDOW_MAX,
  WINDOW_MIN,
  quotaError,
  reasonFor,
  wholeInRange,
  type LimitError,
} from "./helpTypes";
import type { GiftType } from "./types";

/**
 * The bounds a gift row obeys — every one of them the Help section's own, taken
 * rather than restated.
 *
 * A gift's window is a length of time exactly as a request's is, a hundred of
 * anything per window is already "unlimited" in either mechanic, and a `type`
 * is the same identifier the game sends verbatim. Two sets of numbers would be
 * two ideas of a sane value, free to drift apart while the operator learns both
 * — and there is no gift-shaped reason for either to move.
 *
 * They are re-exported so the gift editor knocks on one door: everything the
 * grid needs to bound a box comes from this module, whoever wrote the numbers.
 */
export {
  AMOUNT_MAX,
  AMOUNT_MIN,
  MAX_MAX,
  MAX_MIN,
  TYPE_REGEX,
  WINDOW_MAX,
  WINDOW_MIN,
};

/** What is wrong with a row's cap, if anything. A single key has no half-filled
 *  state, so out of range is the only way to get it wrong. */
export function amountError(row: GiftType): string | undefined {
  return row.max_amount === undefined
    ? undefined
    : (wholeInRange(row.max_amount, AMOUNT_MIN, AMOUNT_MAX) ?? undefined);
}

/** The quota counting every recipient together: how many gifts of this type one
 *  player may send over the last window, to anybody at all. */
export function limitError(row: GiftType): LimitError | undefined {
  return quotaError(row.max_per_window, row.window_seconds);
}

/**
 * The same quota narrowed to one pair of players — and the same rule applied
 * again, deliberately: half of THIS pair is as broken as half of the other, and
 * an operator who learnt the rule once has not learnt a second one.
 *
 * Its own call rather than a flag on the one above, because a type may limit
 * the total, the pair, both or neither, and each verdict paints its own boxes.
 */
export function pairLimitError(row: GiftType): LimitError | undefined {
  return quotaError(row.pair_max_per_window, row.pair_window_seconds);
}

/**
 * Why this list cannot be SAVED, if it cannot. The gift half of
 * `helpTypesError`, blocking on the same three kinds of thing and for the same
 * reasons: a nameless row names no type at all, a duplicate loses one of its
 * two rows the moment the list is read as a map, and half a quota is a setting
 * the server ignores while the designer who typed it believes the type is
 * limited.
 *
 * A name that fails TYPE_REGEX is NOT blocking here either. What the server
 * checks is string equality, so a type named `"Cards"` works perfectly well as
 * long as the game sends `"Cards"` — and this gate disables Save for the WHOLE
 * page, which is too big a hammer for a spelling preference.
 */
export function giftTypesError(types: GiftType[]): string | undefined {
  const seen = new Set<string>();
  for (const row of types) {
    if (!row.type) return "Every gift type needs a name.";
    if (seen.has(row.type)) return `Duplicate gift type “${row.type}”.`;
    seen.add(row.type);
    const limit = limitError(row);
    if (limit) {
      if (limit.pair) {
        return limit.pair === "Set a window"
          ? `Gift type “${row.type}” has a max but no window.`
          : `Gift type “${row.type}” has a window but no max.`;
      }
      const bad = limit.max ?? limit.window ?? "";
      return `Gift type “${row.type}” has a quota ${reasonFor(bad)}.`;
    }
    // Named apart from the quota above, because the two are separately
    // settable and the bar has one line to say which one is broken in.
    const pair = pairLimitError(row);
    if (pair) {
      if (pair.pair) {
        return pair.pair === "Set a window"
          ? `Gift type “${row.type}” has a per-player max but no window.`
          : `Gift type “${row.type}” has a per-player window but no max.`;
      }
      const bad = pair.max ?? pair.window ?? "";
      return `Gift type “${row.type}” has a per-player quota ${reasonFor(bad)}.`;
    }
    // The cap is a single key, so there is no half of one to be missing: a
    // number the server would not read is the only way to get it wrong, said
    // the same way both quotas say it.
    const amount = amountError(row);
    if (amount) {
      return `Gift type “${row.type}” has a max amount ${reasonFor(amount)}.`;
    }
  }
  return undefined;
}
